/**
 * Terms of service. No governing-law clause by the operator's decision: stating a jurisdiction
 * that has not actually been chosen would be worse than leaving it out.
 */
import { pageTitle } from '@thottbot/shared';
import { Link } from 'react-router';
import { BLIZZARD_NOTICE } from '../../components/Footer';
import { CONTACT_EMAIL, OPERATOR, POLICY_UPDATED } from './contact';

export function Terms() {
  return (
    <div className="page legal" data-testid="terms-page">
      <title>{pageTitle('Terms of service')}</title>
      <h1>Terms of service</h1>
      <p className="muted">Last updated {POLICY_UPDATED}.</p>

      <p>
        Thottbot Forever is a free, fan-run database for the World of Warcraft: Forever client,
        operated by {OPERATOR}. Using the site means accepting what is below. If you do not, do
        not use it.
      </p>

      <h2>What this site is, and is not</h2>
      <p>
        It is an unofficial fan site. It is <b>not</b> affiliated with, endorsed by, sponsored by
        or connected to Blizzard Entertainment, nor with the operators of the WoW: Forever client.
        Game data and imagery belong to their owners and appear here under Blizzard's fan site
        guidelines.
      </p>

      <h2>Your account</h2>
      <ul>
        <li>An account is tied to your Battle.net identity. Do not use someone else's.</li>
        <li>You are responsible for what happens under your account.</li>
        <li>
          Accounts, contributions and builds can be suspended or removed if you break these terms.
          Ban evasion by making a new account is itself a breach.
        </li>
      </ul>

      <h2>Contributing</h2>
      <p>When you submit an entry, edit, comment or screenshot, you confirm that:</p>
      <ul>
        <li>you have the right to submit it;</li>
        <li>
          it is, to the best of your knowledge, accurate — the value of a database is that people
          can trust it;
        </li>
        <li>
          it is not spam, advertising, abuse, harassment, malware, or content that is unlawful
          where you are;
        </li>
        <li>
          it does not include personal information about anyone else, and does not include the
          real-world identity of another player.
        </li>
      </ul>
      <p>
        You keep whatever rights you have in what you contribute. By contributing, you grant{' '}
        {OPERATOR} a non-exclusive, worldwide, royalty-free licence to host, display, adapt and
        distribute it as part of the database, including after you stop using the site — otherwise
        one departure could gut a shared record that other people built on. You can ask for your
        BattleTag to be detached from your contributions; see the{' '}
        <Link to="/privacy">privacy policy</Link>.
      </p>
      <p>
        Contributions are moderated. An edit can be revised, merged or reverted by a moderator,
        and nothing you submit is guaranteed to stay up.
      </p>

      <h2>The addon</h2>
      <p>
        The <Link to="/addon">Thottbot addon</Link> is provided as-is. It records observations of
        the game world and nothing about you or your character. Run it at your own risk and within
        whatever rules the client you play on sets — we cannot and do not vouch for your standing
        with any game operator.
      </p>

      <h2>The build optimizer</h2>
      <p>
        Builds are generated by a language model working from this database, and are validated
        against it before being shown. They are <b>suggestions, not advice</b>. The database can
        be wrong, the game can change, and the model can be confidently mistaken. Check anything
        that matters before spending gold or hours on it. Use of the optimizer is rate-limited and
        subject to a daily allowance, which can be changed at any time.
      </p>

      <h2>Acceptable use</h2>
      <ul>
        <li>
          Do not scrape the site in a way that degrades it for other people. There is a{' '}
          <a href="/robots.txt">robots.txt</a> and a <a href="/sitemap.xml">sitemap</a>; use them.
        </li>
        <li>Do not attempt to break, overload or gain unauthorised access to any part of it.</li>
        <li>Do not use it to distribute malware or to phish anyone.</li>
        <li>Do not impersonate another player, a moderator, or the site itself.</li>
      </ul>

      <h2>Copyright</h2>
      <p>
        If you believe material here infringes your copyright, the notice-and-takedown process is
        on the <Link to="/dmca">copyright page</Link>.
      </p>

      <h2>No warranty, and the limit of our liability</h2>
      <p>
        The site is provided "as is" and "as available", without warranties of any kind. It is a
        hobby project run for free: it may be wrong, slow, or simply switched off. To the fullest
        extent the law allows, {OPERATOR} is not liable for any loss arising from your use of the
        site or from reliance on anything it says. Nothing here excludes liability that cannot
        legally be excluded.
      </p>

      <h2>Changes and ending</h2>
      <p>
        These terms can change; the date at the top moves when they do. Features can be added,
        changed or withdrawn, and the site can be discontinued. You can stop using it at any time.
      </p>

      <h2>Contact</h2>
      <p>
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
      </p>

      <h2>Trademarks</h2>
      <p className="muted">{BLIZZARD_NOTICE}</p>
    </div>
  );
}
